import React, { useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Plus, Trash2, Pencil, Heart, Sparkles } from "lucide-react";
import { formatDate, formatTime } from "@/components/common";

const TIMES = [];
for (let h = 7; h <= 23; h++) TIMES.push(`${String(h).padStart(2, "0")}:00`);

export function ActivityPicker({ byCategory, categories, mode = "select", selected, onToggle }) {
    // selected: for "select" -> Set of ids; for "prefs" -> object {id: "love"|"try"}
    const isSel = (id) => (mode === "prefs" ? !!selected[id] : selected.has(id));
    return (
        <div className="space-y-6">
            {categories.map((cat) => (
                <div key={cat.name}>
                    <h3 className="mb-3 font-display text-lg font-700 text-primary">{cat.name}</h3>
                    <div className="flex flex-wrap gap-2">
                        {(byCategory[cat.name] || []).map((a) => {
                            const active = isSel(a.id);
                            const level = mode === "prefs" ? selected[a.id] : null;
                            return (
                                <button
                                    key={a.id}
                                    type="button"
                                    data-testid={`activity-chip-${a.id}`}
                                    onClick={() => onToggle(a)}
                                    className={`group inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors ${
                                        active
                                            ? level === "try"
                                                ? "border-accent bg-accent/10 text-accent"
                                                : "border-primary bg-primary text-primary-foreground"
                                            : "border-border bg-card text-foreground hover:border-primary/40"
                                    }`}
                                >
                                    <span>{a.emoji}</span>
                                    <span className="font-500">{a.name}</span>
                                    {mode === "prefs" && active && (
                                        <span className="ml-1 text-[10px] font-700 uppercase">
                                            {level === "love" ? "Love" : "Try"}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
            {mode === "prefs" && (
                <p className="text-xs text-muted-foreground">
                    Tap once for <span className="font-600 text-primary">I love this</span>, again for{" "}
                    <span className="font-600 text-accent">I'd try this</span>, again to remove.
                </p>
            )}
        </div>
    );
}

export function AvailabilityEditor({ items, onAdd, onDelete, onUpdate, testidPrefix = "avail", max = 5 }) {
    const [dates, setDates] = useState([]);
    const [start, setStart] = useState("16:00");
    const [end, setEnd] = useState("22:00");
    const [editId, setEditId] = useState(null);
    const [busy, setBusy] = useState(false);
    const full = !editId && items.length >= max;
    const remaining = Math.max(1, max - items.length);

    const reset = () => { setDates([]); setStart("16:00"); setEnd("22:00"); setEditId(null); };

    const save = async () => {
        if (!dates.length) { toast.error("Pick at least one date."); return; }
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setBusy(true);
        try {
            if (editId && onUpdate) {
                await onUpdate(editId, { date: dates[0].toISOString().slice(0, 10), start, end, tz });
            } else {
                for (const d of dates) {
                    await onAdd({ date: d.toISOString().slice(0, 10), start, end, tz });
                }
            }
            reset();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const startEdit = (w) => {
        setEditId(w.id);
        setDates([new Date(`${w.date}T00:00:00`)]);
        setStart(w.start); setEnd(w.end);
    };

    const tzAbbr = (w) => {
        if (!w.tz) return "";
        try {
            const d = new Date(`${w.date}T${w.end || "23:00"}:00`);
            const parts = new Intl.DateTimeFormat("en-US", { timeZone: w.tz, timeZoneName: "short" }).formatToParts(d);
            return parts.find((p) => p.type === "timeZoneName")?.value || "";
        } catch { return ""; }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-2xl bg-card p-4 shadow-sm">
                <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-600 text-muted-foreground" data-testid={`${testidPrefix}-count`}>{items.length} of {max} Upcoming Windows</p>
                    {editId && <button type="button" onClick={reset} className="text-xs text-accent hover:underline">Cancel edit</button>}
                </div>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
                    <div>
                        <label className="mb-1 block text-xs font-600 text-muted-foreground">{editId ? "Date" : "Date(s) — pick up to 5"}</label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" data-testid={`${testidPrefix}-date-btn`} className="w-full justify-start rounded-full font-400">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dates.length === 0 ? "Pick date(s)" : dates.length === 1 ? formatDate(dates[0].toISOString().slice(0, 10)) : `${dates.length} dates selected`}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar mode="multiple" max={editId ? 1 : remaining} selected={dates} onSelect={(ds) => setDates(ds || [])} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-600 text-muted-foreground">From</label>
                        <Select value={start} onValueChange={setStart}>
                            <SelectTrigger data-testid={`${testidPrefix}-start`} className="rounded-full"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-60">{TIMES.map((t) => <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-600 text-muted-foreground">To</label>
                        <Select value={end} onValueChange={setEnd}>
                            <SelectTrigger data-testid={`${testidPrefix}-end`} className="rounded-full"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-60">{TIMES.map((t) => <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <Button type="button" onClick={save} disabled={busy || !dates.length || (!editId && full)} data-testid={`${testidPrefix}-add`} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
                        <Plus className="mr-1 h-4 w-4" /> {editId ? "Save" : dates.length > 1 ? `Add ${dates.length}` : "Add"}
                    </Button>
                </div>
                {full && <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testidPrefix}-full`}>You've reached {max} upcoming windows. Remove one to add another.</p>}
                {!editId && items.length > 0 && items.length < max && <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testidPrefix}-room`}>You have room for another upcoming window. Add one above?</p>}
            </div>
            <div className="space-y-2">
                {items.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border px-4 py-4 text-center text-sm text-muted-foreground" data-testid={`${testidPrefix}-empty`}>
                        You're all caught up. Add your next available time so we can keep looking for activities for you.
                    </div>
                )}
                {items.map((w) => (
                    <div key={w.id || `${w.date}-${w.start}-${w.end}`} data-testid={`${testidPrefix}-item`} className="flex items-center justify-between rounded-xl bg-card px-4 py-3 shadow-sm">
                        <div>
                            <p className="font-600 text-primary">{formatDate(w.date)}</p>
                            <p className="text-sm text-muted-foreground">{formatTime(w.start)} – {formatTime(w.end)}{tzAbbr(w) && <span className="ml-1 font-600 text-primary/70">{tzAbbr(w)}</span>}</p>
                        </div>
                        <div className="flex items-center gap-1">
                            {onUpdate && (
                                <Button variant="ghost" size="icon" onClick={() => startEdit(w)} data-testid={`${testidPrefix}-edit`} className="text-muted-foreground hover:text-primary">
                                    <Pencil className="h-4 w-4" />
                                </Button>
                            )}
                            {onDelete && (
                                <Button variant="ghost" size="icon" onClick={() => onDelete(w)} data-testid={`${testidPrefix}-delete`} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
